import * as React from 'react'
import { createPortal } from 'react-dom'
import type { Editor } from '@tiptap/react'
import { CellSelection } from '@tiptap/pm/tables'
import {
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  Heading,
  Merge,
  Split,
  Trash2,
} from 'lucide-react'

import { Button } from '@/tiptap/tiptap-ui-primitive/button'
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '@/tiptap/tiptap-ui-primitive/toolbar'

interface TableBubbleMenuProps {
  editor: Editor | null
}

const MENU_HEIGHT = 36
const GAP = 8

function TableBubbleMenuInner({ editor }: TableBubbleMenuProps) {
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(
    null
  )

  React.useEffect(() => {
    if (!editor) return

    const recompute = () => {
      if (!editor.isEditable) {
        setPos(null)
        return
      }
      const { selection } = editor.state
      const inTable = editor.isActive('table')
      const isCellSelection = selection instanceof CellSelection
      // Show when:
      //   - caret is inside a table (collapsed selection), OR
      //   - the user has selected one or more cells (CellSelection).
      // Hide for normal text-selection inside a cell — the formatting
      // toolbar handles that case.
      if (!inTable) {
        setPos(null)
        return
      }
      if (!selection.empty && !isCellSelection) {
        setPos(null)
        return
      }
      try {
        const coords = editor.view.coordsAtPos(selection.from)
        setPos({
          top: Math.max(
            coords.top + window.scrollY - MENU_HEIGHT - GAP,
            window.scrollY + 8
          ),
          left: coords.left + window.scrollX,
        })
      } catch {
        setPos(null)
      }
    }

    const onScrollOrResize = () => {
      // Only reposition while shown; reuse the same logic.
      recompute()
    }

    recompute()
    editor.on('selectionUpdate', recompute)
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      editor.off('selectionUpdate', recompute)
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [editor])

  if (!editor || !editor.isEditable || !pos) return null

  const addRowBefore = () => editor.chain().focus().addRowBefore().run()
  const addRowAfter = () => editor.chain().focus().addRowAfter().run()
  const addColumnBefore = () => editor.chain().focus().addColumnBefore().run()
  const addColumnAfter = () => editor.chain().focus().addColumnAfter().run()
  const toggleHeaderRow = () => editor.chain().focus().toggleHeaderRow().run()
  const mergeCells = () => editor.chain().focus().mergeCells().run()
  const splitCell = () => editor.chain().focus().splitCell().run()
  const deleteRow = () => editor.chain().focus().deleteRow().run()
  const deleteColumn = () => editor.chain().focus().deleteColumn().run()
  const deleteTable = () => editor.chain().focus().deleteTable().run()

  const canMerge = editor.can().mergeCells()
  const canSplit = editor.can().splitCell()

  return createPortal(
    <div
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        zIndex: 50,
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <Toolbar
        variant='floating'
        style={{
          backgroundColor: 'var(--popover)',
          borderColor: 'var(--border)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <ToolbarGroup>
          <Button
            type='button'
            data-style='ghost'
            className='w-fit !rounded-md border-none h-7 px-2'
            onClick={addRowBefore}
            tooltip='Insert row above'
          >
            <ArrowUpToLine className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            data-style='ghost'
            className='w-fit !rounded-md border-none h-7 px-2'
            onClick={addRowAfter}
            tooltip='Insert row below'
          >
            <ArrowDownToLine className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            data-style='ghost'
            className='w-fit !rounded-md border-none h-7 px-2'
            onClick={addColumnBefore}
            tooltip='Insert column left'
          >
            <ArrowLeftToLine className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            data-style='ghost'
            className='w-fit !rounded-md border-none h-7 px-2'
            onClick={addColumnAfter}
            tooltip='Insert column right'
          >
            <ArrowRightToLine className='h-3.5 w-3.5' />
          </Button>
        </ToolbarGroup>

        <ToolbarSeparator />

        <ToolbarGroup>
          <Button
            type='button'
            data-style='ghost'
            className='w-fit !rounded-md border-none h-7 px-2'
            onClick={toggleHeaderRow}
            tooltip='Toggle header row'
          >
            <Heading className='h-3.5 w-3.5' />
          </Button>
          {canMerge && (
            <Button
              type='button'
              data-style='ghost'
              className='w-fit !rounded-md border-none h-7 px-2'
              onClick={mergeCells}
              tooltip='Merge cells'
            >
              <Merge className='h-3.5 w-3.5' />
            </Button>
          )}
          {canSplit && (
            <Button
              type='button'
              data-style='ghost'
              className='w-fit !rounded-md border-none h-7 px-2'
              onClick={splitCell}
              tooltip='Split cell'
            >
              <Split className='h-3.5 w-3.5' />
            </Button>
          )}
        </ToolbarGroup>

        <ToolbarSeparator />

        <ToolbarGroup>
          <Button
            type='button'
            data-style='ghost'
            className='w-fit gap-1.5 !rounded-md border-none h-7 px-2'
            onClick={deleteRow}
            tooltip='Delete row'
          >
            <Trash2 className='h-3.5 w-3.5' />
            <span className='text-xs whitespace-nowrap'>Row</span>
          </Button>
          <Button
            type='button'
            data-style='ghost'
            className='w-fit gap-1.5 !rounded-md border-none h-7 px-2'
            onClick={deleteColumn}
            tooltip='Delete column'
          >
            <Trash2 className='h-3.5 w-3.5' />
            <span className='text-xs whitespace-nowrap'>Col</span>
          </Button>
          <Button
            type='button'
            data-style='ghost'
            className='w-fit gap-1.5 !rounded-md border-none h-7 px-2 text-destructive hover:text-destructive'
            onClick={deleteTable}
            tooltip='Delete table'
          >
            <Trash2 className='h-3.5 w-3.5' />
            <span className='text-xs whitespace-nowrap'>Table</span>
          </Button>
        </ToolbarGroup>
      </Toolbar>
    </div>,
    document.body
  )
}

export const TableBubbleMenu = React.memo(TableBubbleMenuInner)
export default TableBubbleMenu
